import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import classnames from "classnames";
import { FileSignature, ShieldAlert } from "lucide-react";

import useKycBanner from "./use-kyc-banner";

const URGENT_THRESHOLD_DAYS = 5;

// One slim row per outstanding requirement: icon, what is missing, the window
// countdown beside it, and the action on the far right. Rows share a single
// bordered strip so both prompts together stay shorter than one boxed banner.
const RequirementRow = ({ icon: Icon, label, meta, urgent, to, action }) => (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
        <Icon className={classnames("h-4 w-4 shrink-0", urgent ? "text-danger" : "text-warning-foreground")} />
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {meta && (
            <span className={classnames("text-xs", urgent ? "font-medium text-danger" : "text-muted")}>{meta}</span>
        )}
        <Link
            to={to}
            className="ml-auto rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-white hover:bg-primary/90">
            {action} →
        </Link>
    </div>
);

RequirementRow.propTypes = {
    icon  : PropTypes.elementType.isRequired,
    label : PropTypes.string.isRequired,
    meta  : PropTypes.string,
    urgent: PropTypes.bool,
    to    : PropTypes.string.isRequired,
    action: PropTypes.string.isRequired
};

// Payouts are gated on both KYC and the brand's contract, so every publisher
// screen that leads to money owed carries this prompt until both clear. It is
// self-contained — it reads :publisherId off the route and fetches its own
// standing — so pages only drop it in. Renders nothing while loading or once
// there is nothing left to chase.
const KycBanner = ({ className = "" }) => {
    const {
        publisherId,
        loading,
        kycPending,
        contractPending,
        daysRemaining,
        windowDays
    } = useKycBanner();

    if (loading || (!kycPending && !contractPending)) return null;

    // The countdown only reads as urgent while KYC is what's outstanding; a
    // pending contract alone leaves the strip on its default warning tone.
    const hasCountdown = kycPending && typeof daysRemaining === "number";
    const isUrgent = hasCountdown && daysRemaining <= URGENT_THRESHOLD_DAYS;
    const windowNote = hasCountdown
        ? `KYC window: ${daysRemaining}${windowDays ? ` of ${windowDays}` : ""} days remaining`
        : null;

    return (
        <div
            role="status"
            className={classnames(
                "divide-y overflow-hidden rounded-lg border",
                isUrgent
                    ? "divide-danger/20 border-danger/40 bg-danger/5"
                    : "divide-warning/25 border-warning/40 bg-warning/10",
                className
            )}>
            {kycPending && (
                <RequirementRow
                    icon={ShieldAlert}
                    label="Complete your KYC to receive payouts"
                    meta={windowNote}
                    urgent={isUrgent}
                    to={`/publisher/${publisherId}/kyc`}
                    action="Start KYC" />
            )}
            {contractPending && (
                <RequirementRow
                    icon={FileSignature}
                    label="Acknowledge your contract to receive payouts"
                    to={`/publisher/${publisherId}/contracts`}
                    action="Review contract" />
            )}
        </div>
    );
};

KycBanner.propTypes = {
    className: PropTypes.string
};

export default KycBanner;
